import {
  emptyAllocation,
  type StatAllocation,
  type StatId,
  STAT_IDS,
} from "@/game/model/character-stats";
import {
  cumulativeExpForLevel,
  levelFromTotalExp,
  PROGRESSION_MAX_LEVEL,
  statPointsForLevelUp,
  xpToNextLevel,
} from "@/game/model/progression-tables";

export const PROGRESSION_STORAGE_VERSION = 1 as const;

export type CharacterProgression = {
  version: typeof PROGRESSION_STORAGE_VERSION;
  totalExp: number;
  level: number;
  statPointsAvailable: number;
  allocated: StatAllocation;
  maxLevel: number;
};

const STORAGE_PREFIX = "mind-survival/progression/v1";

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

export function createInitialProgression(): CharacterProgression {
  return {
    version: PROGRESSION_STORAGE_VERSION,
    totalExp: 0,
    level: 1,
    statPointsAvailable: 0,
    allocated: emptyAllocation(),
    maxLevel: PROGRESSION_MAX_LEVEL,
  };
}

function normalizeAllocation(raw: unknown): StatAllocation {
  const base = emptyAllocation();
  if (!raw || typeof raw !== "object") return base;
  const parsed = raw as Partial<StatAllocation>;
  for (const id of STAT_IDS) {
    const n = parsed[id];
    if (typeof n === "number" && Number.isFinite(n) && n >= 0) {
      base[id] = Math.floor(n);
    }
  }
  return base;
}

export function normalizeProgression(raw: unknown): CharacterProgression {
  const base = createInitialProgression();
  if (!raw || typeof raw !== "object") return base;
  const p = raw as Partial<CharacterProgression>;
  const totalExp =
    typeof p.totalExp === "number" && Number.isFinite(p.totalExp)
      ? Math.max(0, Math.floor(p.totalExp))
      : 0;
  const maxLevel =
    typeof p.maxLevel === "number" && Number.isFinite(p.maxLevel)
      ? Math.min(PROGRESSION_MAX_LEVEL, Math.max(1, Math.floor(p.maxLevel)))
      : PROGRESSION_MAX_LEVEL;
  const level = Math.min(maxLevel, levelFromTotalExp(totalExp));
  const statPointsAvailable =
    typeof p.statPointsAvailable === "number" && Number.isFinite(p.statPointsAvailable)
      ? Math.max(0, Math.floor(p.statPointsAvailable))
      : 0;
  return {
    version: PROGRESSION_STORAGE_VERSION,
    totalExp,
    level,
    statPointsAvailable,
    allocated: normalizeAllocation(p.allocated),
    maxLevel,
  };
}

export function loadCharacterProgression(userId: string): CharacterProgression {
  const raw = localStorage.getItem(storageKey(userId));
  if (!raw) return createInitialProgression();
  try {
    return normalizeProgression(JSON.parse(raw));
  } catch {
    localStorage.removeItem(storageKey(userId));
    return createInitialProgression();
  }
}

export function saveCharacterProgression(
  userId: string,
  progression: CharacterProgression,
): void {
  localStorage.setItem(storageKey(userId), JSON.stringify(progression));
  import("@/cloud/player-cloud-scheduler").then((m) =>
    m.schedulePlayerCloudPush(userId, "gameState"),
  );
}

export type GrantExpResult = {
  progression: CharacterProgression;
  levelsGained: number;
};

export function grantExp(
  progression: CharacterProgression,
  amount: number,
): GrantExpResult {
  const add = Math.max(0, Math.floor(amount));
  if (add <= 0) {
    return { progression, levelsGained: 0 };
  }

  let totalExp = progression.totalExp + add;
  let level = progression.level;
  let statPointsAvailable = progression.statPointsAvailable;
  const maxLevel = progression.maxLevel;
  let levelsGained = 0;

  let nextLevel = levelFromTotalExp(totalExp);
  while (nextLevel > level && level < maxLevel) {
    const newLevel = level + 1;
    statPointsAvailable += statPointsForLevelUp(newLevel);
    level = newLevel;
    levelsGained++;
    nextLevel = levelFromTotalExp(totalExp);
  }

  if (level >= maxLevel) {
    level = maxLevel;
  }

  const next: CharacterProgression = {
    ...progression,
    totalExp,
    level,
    statPointsAvailable,
  };
  return { progression: next, levelsGained };
}

export const STAT_ALLOCATE_STEP = 10;

export function allocateStatPoint(
  progression: CharacterProgression,
  statId: StatId,
): CharacterProgression {
  if (progression.statPointsAvailable <= 0) return progression;
  if (!STAT_IDS.includes(statId)) return progression;
  return {
    ...progression,
    statPointsAvailable: progression.statPointsAvailable - 1,
    allocated: {
      ...progression.allocated,
      [statId]: progression.allocated[statId] + 1,
    },
  };
}

export function allocateStatPoints(
  progression: CharacterProgression,
  statId: StatId,
  points = STAT_ALLOCATE_STEP,
): CharacterProgression {
  if (points <= 0) return progression;
  if (progression.statPointsAvailable < points) return progression;
  let cur = progression;
  for (let i = 0; i < points; i += 1) {
    const next = allocateStatPoint(cur, statId);
    if (next === cur) return cur;
    cur = next;
  }
  return cur;
}

export function totalAllocatedStatPoints(allocated: StatAllocation): number {
  return STAT_IDS.reduce((sum, id) => sum + allocated[id], 0);
}

/** AP earned from reaching `level` (Lv2…level). */
export function statPointsBudgetForLevel(level: number, maxLevel = PROGRESSION_MAX_LEVEL): number {
  const lv = Math.min(maxLevel, Math.max(1, Math.floor(level)));
  let sum = 0;
  for (let n = 2; n <= lv; n += 1) {
    sum += statPointsForLevelUp(n);
  }
  return sum;
}

export type StatAllocationValidation =
  | { ok: true }
  | { ok: false; message: string };

export function validateStatAllocationProgression(
  progression: CharacterProgression,
): StatAllocationValidation {
  if (!Number.isFinite(progression.statPointsAvailable) || progression.statPointsAvailable < 0) {
    return { ok: false, message: "AP คงเหลือไม่ถูกต้อง (ติดลบ)" };
  }
  for (const id of STAT_IDS) {
    const n = progression.allocated[id];
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
      return { ok: false, message: `ค่าสเตต «${id}» ไม่ถูกต้อง` };
    }
  }
  const spent = totalAllocatedStatPoints(progression.allocated);
  const budget = statPointsBudgetForLevel(progression.level, progression.maxLevel);
  const pool = spent + progression.statPointsAvailable;
  if (pool !== budget) {
    return {
      ok: false,
      message: `AP ไม่สมดุล: ใช้ ${spent} + เหลือ ${progression.statPointsAvailable} ≠ งบ ${budget} ตามเลเวล`,
    };
  }
  return { ok: true };
}

export function saveCharacterProgressionWithStatValidation(
  userId: string,
  progression: CharacterProgression,
): StatAllocationValidation {
  const check = validateStatAllocationProgression(progression);
  if (!check.ok) return check;
  saveCharacterProgression(userId, progression);
  return { ok: true };
}

export function cloneCharacterProgression(progression: CharacterProgression): CharacterProgression {
  return {
    ...progression,
    allocated: { ...progression.allocated },
  };
}

export function resetAllocatedStats(progression: CharacterProgression): CharacterProgression {
  const spent = STAT_IDS.reduce((sum, id) => sum + progression.allocated[id], 0);
  return {
    ...progression,
    statPointsAvailable: progression.statPointsAvailable + spent,
    allocated: emptyAllocation(),
  };
}

export function progressionXpDisplay(progression: CharacterProgression): {
  totalExp: number;
  xpIntoLevel: number;
  xpToNext: number;
} {
  const totalExp = progression.totalExp;
  const level = progression.level;
  if (level >= progression.maxLevel) {
    return { totalExp, xpIntoLevel: 0, xpToNext: 0 };
  }
  const xpToNext = xpToNextLevel(level);
  const levelStart = cumulativeExpForLevel(level);
  const xpIntoLevel = Math.min(xpToNext, Math.max(0, totalExp - levelStart));
  return { totalExp, xpIntoLevel, xpToNext };
}
